import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedPage from '../components/shared/AnimatedPage';
import AppFooter from '../components/shared/AppFooter';
import { BouncingBalls } from '../components/shared/BouncingBalls';
import { useAuthStore } from '../stores/authStore';
import { useDarkStore } from '../app/store/darkStore';
import { signOut } from '../hooks/useAuth';

const STAGES = [
  { num: 1, name: 'The OG Origin Story', emoji: '🌟', desc: 'Dream up your big idea and give it a name.' },
  { num: 2, name: 'The Struggle Is Real', emoji: '🔍', desc: 'Find real problems worth solving.' },
  { num: 3, name: "From 'Ugh!' To 'Aha!'", emoji: '🕵️', desc: 'Turn problems into opportunities.' },
  { num: 4, name: 'Who Will Buy This?', emoji: '🛠️', desc: 'Build and test your first prototype.' },
  { num: 5, name: 'Show Me The Money!!', emoji: '💰', desc: 'Figure out your pricing and costs.' },
  { num: 6, name: 'The Game Plan', emoji: '📊', desc: 'Craft your pitch and strategy.' },
];

const EXAMPLES = [
  {
    name: 'EcoSnacks',
    creator: 'Priya, age 12',
    desc: 'Sustainable snack boxes delivered to schools — made from locally sourced ingredients.',
    accent: 'mint',
  },
  {
    name: 'PetPal',
    creator: 'James, age 14',
    desc: 'A neighbourhood pet-sitting service with an app for booking and reviews.',
    accent: 'amber',
  },
  {
    name: 'StudyBuddy',
    creator: 'Amara, age 11',
    desc: 'Flashcard kits for younger kids, designed and illustrated by teens.',
    accent: 'violet',
  },
];

// Dark-palette colours for the bouncing-ball backdrop so they feel on-brand.
const BALL_COLORS = [
  'rgba(245, 158, 11, 0.70)',  // amber
  'rgba(16, 185, 129, 0.70)',  // mint
  'rgba(139, 92, 246, 0.70)',  // violet
  'rgba(244, 114, 114, 0.55)', // coral
  'rgba(255, 255, 255, 0.35)', // faint white spark
];

const ACCENT_CLASS: Record<string, { ring: string; text: string; chip: string }> = {
  amber: {
    ring: 'border-amber/40',
    text: 'text-amber',
    chip: 'bg-amber/10 text-amber',
  },
  mint: {
    ring: 'border-mint/40',
    text: 'text-mint',
    chip: 'bg-mint/10 text-mint',
  },
  violet: {
    ring: 'border-violet/40',
    text: 'text-violet-glow',
    chip: 'bg-violet/10 text-violet-glow',
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: 'easeOut' as const },
  }),
};

export default function LandingPage() {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);

  const explorerName = useDarkStore((s) => s.explorerName);
  const stages = useDarkStore((s) => s.stages);
  const hasStarted = !!explorerName || stages.some((s) => s.status === 'done');
  const ctaHref = hasStarted ? '/home/plans' : '/home/challenge/1';
  const ctaLabel = hasStarted ? 'Continue your journey' : 'Start your business';

  const displayName =
    profile?.explorerName || profile?.displayName || user?.email || '';

  if (user && profile?.hasCompletedSignUp) {
    return <Navigate to="/home" replace />;
  }

  return (
    <AnimatedPage>
      <div className="relative min-h-screen overflow-hidden bg-base text-ink">
        {/* Bouncing-ball backdrop — fixed so it stays visible while scrolling. */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-0"
          style={{ background: 'radial-gradient(ellipse at top, #1A1F35 0%, #0D0F1C 60%)' }}
        >
          <BouncingBalls
            numBalls={120}
            colors={BALL_COLORS}
            minRadius={0.6}
            maxRadius={3.2}
            speed={0.45}
            bounceDamping={1}
            friction={1}
            interactive
            interactionRadius={120}
            interactionScale={5}
          />
        </div>

        {/* Dark topbar with Sign In / Sign Out */}
        <header className="relative z-20 border-b border-white/5 bg-base/70 backdrop-blur-md">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
            <Link
              to="/"
              className="font-marker text-2xl tracking-wide text-amber drop-shadow-[0_0_8px_rgba(245,166,35,0.35)] transition-transform hover:scale-105"
            >
              OMore
            </Link>

            <nav className="flex items-center gap-3">
              {user && profile?.hasCompletedSignUp && (
                <Link
                  to="/spotlight"
                  className="hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-2 font-marker text-sm text-ink/80 transition-colors hover:bg-white/10 md:inline-flex"
                >
                  Spotlight
                </Link>
              )}
              {user && hasStarted && (
                <Link
                  to="/home/plans"
                  className="hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-2 font-marker text-sm text-ink/80 transition-colors hover:bg-white/10 md:inline-flex"
                >
                  My Plans
                </Link>
              )}

              {user ? (
                <>
                  <Link
                    to="/account"
                    className="rounded-2xl border border-amber/30 bg-amber/10 px-4 py-2 font-marker text-sm text-amber transition-colors hover:bg-amber/20"
                  >
                    {displayName}
                  </Link>
                  <button
                    onClick={() => signOut()}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 font-marker text-sm text-ink/60 transition-colors hover:bg-white/10"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth?mode=signin"
                  className="rounded-2xl border border-amber/40 bg-amber/10 px-4 py-2 font-marker text-sm text-amber shadow-glow-amber transition-colors hover:bg-amber/20"
                >
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        </header>

        {/* Page content — sits above the bouncing-ball backdrop. */}
        <main className="relative z-10">
          {/* Hero */}
          <section className="relative overflow-hidden">
            <div className="mx-auto flex max-w-7xl flex-col items-center px-4 pb-20 pt-16 text-center md:flex-row md:pb-28 md:pt-24 md:text-left">
              <div className="md:w-1/2">
                <h1 className="font-marker text-5xl leading-tight text-ink md:text-6xl lg:text-7xl">
                  Build Your Business.
                  <br />
                  <span className="text-amber drop-shadow-[0_0_14px_rgba(245,158,11,0.35)]">
                    Change the World.
                  </span>
                </h1>
                <p className="mt-5 max-w-lg font-gill text-lg text-ink/75 md:text-xl">
                  The gamified business builder that turns your spark of an idea
                  into a real plan — across six playful stages.
                </p>

                {user && (
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:justify-start">
                    <Link
                      to={ctaHref}
                      className="inline-flex min-h-touch items-center rounded-2xl border border-amber bg-amber px-7 py-3 font-marker text-lg text-base shadow-glow-amber transition-transform hover:scale-105 active:scale-95"
                    >
                      {ctaLabel} →
                    </Link>
                  </div>
                )}
              </div>

              <div className="mt-12 flex items-center justify-center md:mt-0 md:w-1/2">
                <div className="relative flex h-64 w-64 items-center justify-center rounded-[2rem] border border-amber/25 bg-amber/5 text-8xl shadow-glow-amber backdrop-blur md:h-80 md:w-80">
                  🚀
                </div>
              </div>
            </div>
          </section>

          {/* 6-Stage preview */}
          <section className="py-16 md:py-24">
            <div className="mx-auto max-w-7xl px-4">
              <h2 className="text-center font-marker text-4xl text-ink md:text-5xl">
                Your 6-Stage Journey
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-center font-gill text-ink/70">
                From a spark of an idea to a real business plan — each stage
                builds on the last.
              </p>
              <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {STAGES.map((stage, i) => (
                  <motion.div
                    key={stage.num}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-40px' }}
                    variants={cardVariants}
                    className="rounded-3xl border border-white/10 bg-surface/70 p-6 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-amber/40"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-amber/40 bg-amber/10 font-marker text-lg text-amber">
                        {stage.num}
                      </span>
                      <span className="text-2xl">{stage.emoji}</span>
                    </div>
                    <h3 className="mt-3 font-marker text-xl text-ink">
                      {stage.name}
                    </h3>
                    <p className="mt-1 font-gill text-sm text-ink/65">
                      {stage.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Kid examples */}
          <section className="py-16 md:py-24">
            <div className="mx-auto max-w-7xl px-4">
              <h2 className="text-center font-marker text-4xl text-ink md:text-5xl">
                See What Kids Are Building
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-center font-gill text-ink/70">
                Real ideas from young entrepreneurs just like you.
              </p>
              <div className="mt-12 grid gap-6 md:grid-cols-3">
                {EXAMPLES.map((ex, i) => {
                  const a = ACCENT_CLASS[ex.accent] ?? ACCENT_CLASS.amber;
                  return (
                    <motion.div
                      key={ex.name}
                      custom={i}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: '-40px' }}
                      variants={cardVariants}
                      className={`rounded-3xl border bg-surface/70 p-6 backdrop-blur-md transition-transform hover:-translate-y-0.5 ${a.ring}`}
                    >
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl font-marker text-xl ${a.chip}`}
                      >
                        {ex.name[0]}
                      </div>
                      <h3 className={`mt-4 font-marker text-xl ${a.text}`}>
                        {ex.name}
                      </h3>
                      <p className="font-gill text-sm text-ink/55">{ex.creator}</p>
                      <p className="mt-2 font-gill text-sm text-ink/75">
                        {ex.desc}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Bottom CTA — only for signed-out visitors */}
          {!user && (
            <section className="py-20 md:py-24">
              <div className="mx-auto max-w-2xl rounded-[2rem] border border-amber/30 bg-gradient-to-br from-amber/15 via-violet/10 to-mint/10 px-6 py-14 text-center backdrop-blur-md md:py-16">
                <h2 className="font-marker text-4xl text-ink md:text-5xl">
                  Ready to launch?
                </h2>
                <p className="mx-auto mt-3 max-w-md font-gill text-ink/75">
                  Join hundreds of young entrepreneurs building real businesses.
                </p>
                <Link
                  to="/auth?mode=signin"
                  className="mt-8 inline-flex min-h-touch items-center rounded-2xl border border-amber bg-amber px-8 py-3 font-marker text-lg text-base shadow-glow-amber transition-transform hover:scale-105 active:scale-95"
                >
                  Get started →
                </Link>
              </div>
            </section>
          )}

          <AppFooter />

          {/* Mobile sticky CTA */}
          {user && (
            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-base/85 p-3 backdrop-blur-md md:hidden">
              <Link
                to={ctaHref}
                className="flex min-h-touch w-full items-center justify-center rounded-2xl border border-amber bg-amber py-3 font-marker text-base text-base shadow-glow-amber"
              >
                {ctaLabel} →
              </Link>
            </div>
          )}
        </main>
      </div>
    </AnimatedPage>
  );
}
